import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});

// Request interceptor: attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('dw_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Paths that should NEVER trigger auto-refresh (auth endpoints + initial checks)
const SKIP_REFRESH_PATHS = ['/auth/login', '/auth/register', '/auth/me', '/auth/refresh', '/auth/logout', '/auth/google', '/auth/forgot-password', '/auth/reset-password'];

// Response interceptor: handle token expiry with auto-refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestPath = originalRequest?.url || '';

    // Skip refresh for auth endpoints and already-retried requests
    const isAuthPath = SKIP_REFRESH_PATHS.some(p => requestPath.includes(p));
    if (isAuthPath || originalRequest._retry || error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Only attempt refresh for token-related 401s
    const detail = error.response?.data?.detail || '';
    const isTokenError = detail.includes('expired') || detail.includes('Invalid token') || detail === 'Not authenticated';
    if (!isTokenError) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        if (token) originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      }).catch(err => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
        {}
      );

      const newToken = refreshResponse.data?.token;
      if (newToken) {
        localStorage.setItem('dw_token', newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }

      isRefreshing = false;
      processQueue(null, newToken);
      return api(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      processQueue(refreshError, null);
      localStorage.removeItem('dw_token');
      // Only redirect if user is on a protected page (not login/register/landing)
      const path = window.location.pathname;
      if (path !== '/login' && path !== '/' && path !== '/register' && !path.startsWith('/storefront')) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    }
  }
);

export default api;
